import { ChangeDetectorRef, Component } from '@angular/core';
import {
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  ValidationErrors,
  Validators,
} from '@angular/forms';

import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { merge, Subject, tap } from 'rxjs';
import { take, takeUntil } from 'rxjs/operators';

import { slugValidator } from '@recruitee/common';
import { HelpArticlesDirective } from '@recruitee/help-articles';
import { I18nService, TranslatePipe } from '@recruitee/i18n';
import { JobsActions } from '@recruitee/offers-data';
import {
  ButtonComponent,
  ButtonLoadingDirective,
  DialogBodyComponent,
  DialogComponentI,
  DialogFooterComponent,
  DialogHeaderComponent,
  DialogRef,
  FormErrorDirective,
  FormFieldComponent,
  InputComponent,
  RtTooltip,
  TruncateComponent,
} from '@recruitee/user-interface';
import { LifeCycle } from '@recruitee/utils';

import { JobsAnalytics } from '../../analytics/jobs.analytics';

type DialogData = {
  offerSlug: string;
  companyUrl: string;
  offerId: number;
};

interface EditJobUrlForm {
  offerSlug: FormControl<string | null>;
}

@Component({
  selector: 'rt-edit-job-url-dialog',
  standalone: true,
  imports: [
    DialogHeaderComponent,
    TranslatePipe,
    ButtonComponent,
    ButtonLoadingDirective,
    DialogFooterComponent,
    DialogBodyComponent,
    FormFieldComponent,
    FormsModule,
    InputComponent,
    ReactiveFormsModule,
    TruncateComponent,
    FormErrorDirective,
    RtTooltip,
    HelpArticlesDirective,
  ],
  templateUrl: './edit-job-url-dialog.component.html',
  styleUrls: ['./edit-job-url-dialog.component.less'],
})
export class EditJobUrlDialogComponent implements DialogComponentI<DialogData, any> {
  public form: FormGroup<EditJobUrlForm>;
  public companyUrl: string;
  public loading = false;
  public errors: ValidationErrors = {
    slug: this.i18n.translate('app.offer_form_actions.edit_job_url.errors.slug'),
    slugPattern: this.i18n.translate('app.offer_form_actions.edit_job_url.errors.slug_pattern'),
  };
  private offerId: number;

  @LifeCycle('onDestroy')
  private ngOnDestroy$: Subject<void>;

  constructor(
    private dialog: DialogRef<any>,
    private cdRef: ChangeDetectorRef,
    private store: Store,
    private actions$: Actions,
    private i18n: I18nService,
    private jobsAnalytics: JobsAnalytics,
  ) {}

  public setDialogData(data: DialogData) {
    this.companyUrl = data.companyUrl;
    this.offerId = data.offerId;
    this.form = new FormGroup({
      offerSlug: new FormControl(data.offerSlug, [Validators.required, slugValidator]),
    });
  }

  public ngOnInit() {
    this.form.valueChanges
      .pipe(takeUntil(this.ngOnDestroy$))
      .subscribe(() => this.cdRef.detectChanges());
  }

  public cancel() {
    this.dialog.close();
  }

  public save() {
    const newSlug = this.form.controls.offerSlug.value;

    if (newSlug) {
      this.jobsAnalytics.trackSaveNewUrl();
      this.loading = true;
      this.store.dispatch(JobsActions.updateSlug({ payload: { id: this.offerId, slug: newSlug } }));

      merge(
        this.actions$.pipe(
          ofType(JobsActions.updateSlugSuccess),
          tap(() => {
            this.loading = false;
            this.dialog.close();
          }),
        ),
        this.actions$.pipe(
          ofType(JobsActions.updateSlugFailed),
          tap(({ payload: { error } }) => {
            this.loading = false;
            const errors = Object.keys(error.error.resources);
            errors.forEach(key => {
              this.form.controls.offerSlug.setErrors({
                [key]: true,
              });
            });
            this.cdRef.detectChanges();
          }),
        ),
      )
        .pipe(take(1))
        .subscribe();
    }
  }

  public onBlurOfferSlugField() {
    const offerSlugControl = this.form.controls.offerSlug;
    const offerSlugValue = offerSlugControl.value;
    if (offerSlugValue) {
      const newOfferSlugValue = offerSlugValue.trim().replace(/\s+/g, '-').replace(/-+/g, '-');
      offerSlugControl.setValue(newOfferSlugValue);
      this.cdRef.detectChanges();
    }
  }
}
